import { Link } from "react-router-dom";
import {
  DELIVERY_MODEL,
  REGIONS,
  difyPathForService,
  diyPathForService,
  diwyPathForService,
  formatAud,
  addGst,
  type ServiceOffering,
  type ServiceRegion,
  groupsForRegion,
  servicesForRegion,
} from "@shared/types";
import { ServiceGlyph } from "../components/Visuals";

/** Hub: pick Australia or Singapore. */
export function ServicesHubPage() {
  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div>
            <h2>Services by market</h2>
            <p>
              Choose your region. Every offering is available three ways:{" "}
              <strong>DIY</strong> (tools), <strong>DIWY</strong> (guided with us), or{" "}
              <strong>DIFY</strong> (we deliver).
            </p>
          </div>
        </div>

        <div className="delivery-strip" aria-label="Delivery model">
          {(Object.keys(DELIVERY_MODEL) as Array<keyof typeof DELIVERY_MODEL>).map((key) => {
            const t = DELIVERY_MODEL[key];
            return (
              <div key={key} className="delivery-strip-item">
                <span className="tier-code">{t.code}</span>
                <strong>{t.name}</strong>
                <p>{t.blurb}</p>
              </div>
            );
          })}
        </div>

        <div className="region-pick-grid">
          {(Object.keys(REGIONS) as ServiceRegion[]).map((code) => {
            const r = REGIONS[code];
            return (
              <Link key={code} to={r.path} className="region-pick-card">
                <div className="region-pick-flag">{code.toUpperCase()}</div>
                <h3>{r.name}</h3>
                <p>{r.tagline}</p>
                <ul className="region-focus-list">
                  {r.focus.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <span className="btn btn-primary btn-sm">View {r.name} services</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Regional catalogue: /au/services or /sg/services */
export function RegionalServicesPage({ region }: { region: ServiceRegion }) {
  const meta = REGIONS[region];
  const other: ServiceRegion = region === "au" ? "sg" : "au";
  const groups = groupsForRegion(region);
  const services = servicesForRegion(region);

  return (
    <section className="section">
      <div className="container">
        <div className="region-switcher">
          <Link to="/services" className="muted">
            All markets
          </Link>
          <span aria-hidden="true">·</span>
          <span className="region-switcher-active">{meta.name}</span>
          <span aria-hidden="true">·</span>
          <Link to={REGIONS[other].path}>{REGIONS[other].name} services</Link>
        </div>

        <div className="section-head">
          <div>
            <p className="eyebrow">{meta.code.toUpperCase()} · EcoEng.net</p>
            <h2>{meta.name} services</h2>
            <p>{meta.tagline}</p>
          </div>
          <div className="tier-legend" aria-label="How you can engage">
            <span>
              <strong>DIY</strong> tools
            </span>
            <span>
              <strong>DIWY</strong> guided
            </span>
            <span>
              <strong>DIFY</strong> we deliver
            </span>
          </div>
        </div>

        <div className="delivery-strip delivery-strip-compact" aria-label="Delivery model">
          {(Object.keys(DELIVERY_MODEL) as Array<keyof typeof DELIVERY_MODEL>).map((key) => {
            const t = DELIVERY_MODEL[key];
            return (
              <Link key={key} to={t.path} className="delivery-strip-item delivery-strip-link">
                <span className="tier-code">{t.code}</span>
                <strong>{t.name}</strong>
                <p>{t.blurb}</p>
              </Link>
            );
          })}
        </div>

        {groups.map((group) => {
          const items = services.filter((s) => s.group === group.id);
          if (!items.length) return null;
          return (
            <div key={group.id} className="service-group-block">
              <div className="service-group-head">
                <h3>{group.title}</h3>
                <p className="muted">{group.blurb}</p>
              </div>
              <div className="service-offer-grid">
                {items.map((s) => (
                  <ServiceOfferCard key={s.id} service={s} />
                ))}
              </div>
            </div>
          );
        })}

        <div className="card service-cta-bar">
          <div>
            <h3 style={{ margin: 0 }}>Not sure which tier?</h3>
            <p className="muted" style={{ margin: "0.35rem 0 0" }}>
              Start with DIY if you need it today. Choose DIWY for a reviewed pack. Choose DIFY when
              the risk is real.
            </p>
          </div>
          <div className="service-cta-actions">
            <Link className="btn btn-outline btn-sm" to="/resources">
              DIY library
            </Link>
            <Link className="btn btn-outline btn-sm" to="/reports">
              DIWY reports
            </Link>
            <Link className="btn btn-primary btn-sm" to="/contact">
              DIFY enquiry
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceOfferCard({ service }: { service: ServiceOffering }) {
  const listed = service.listedPriceCents
    ? addGst(service.listedPriceCents)
    : null;
  const plusGst = service.listedPricePlusGst && service.listedPriceCents;

  return (
    <article className="service-offer-card">
      <div className="service-offer-top">
        <ServiceGlyph name={service.name} />
        <div>
          <div className="short">{service.short}</div>
          <h3>{service.name}</h3>
        </div>
      </div>
      <p className="service-offer-blurb">{service.blurb}</p>
      {listed && plusGst ? (
        <p className="price" style={{ margin: "0.75rem 0" }}>
          {formatAud(service.listedPriceCents!)} + GST{" "}
          <small>
            ({formatAud(listed.gstCents)} GST, {formatAud(listed.totalCents)} incl. GST)
          </small>
        </p>
      ) : listed ? (
        <p className="price" style={{ margin: "0.75rem 0" }}>
          {formatAud(service.listedPriceCents!)} <small>ex GST</small>
        </p>
      ) : null}
      {plusGst ? (
        <Link className="btn btn-primary btn-sm" to={difyPathForService(service.id)} style={{ marginBottom: "0.85rem" }}>
          Enquire for this application
        </Link>
      ) : null}
      <div className="tier-matrix" role="list">
        <div className="tier-row" role="listitem">
          <Link to={diyPathForService(service.id)} className="tier-pill diy">
            DIY
          </Link>
          <span>{service.tiers.diy}</span>
        </div>
        <div className="tier-row" role="listitem">
          <Link to={diwyPathForService(service.id)} className="tier-pill diwy">
            DIWY
          </Link>
          <span>{service.tiers.diwy}</span>
        </div>
        <div className="tier-row" role="listitem">
          <Link to={difyPathForService(service.id)} className="tier-pill dify">
            DIFY
          </Link>
          <span>{service.tiers.dify}</span>
        </div>
      </div>
    </article>
  );
}

/** Back-compat export used if anything still imports ServicesPage. */
export function ServicesPage() {
  return <ServicesHubPage />;
}
